import {
  Banner,
  Button,
  ButtonGroup,
  DataTable,
  Divider,
  LegacyCard,
  LegacyStack,
  Text,
  TextField,
} from '@shopify/polaris'
import { DeleteIcon } from '@shopify/polaris-icons'
import { useState } from 'react'
import useSWR, { mutate } from 'swr'

import { AdaptiveModal } from '../common/AdaptiveModal'
import { backendFetchResultData, Result } from '../common/api'
import { ApiAction, ApiActionBanner, useApiActionState } from '../common/ApiAction'
import { ApiActionDialog } from '../common/ApiActionDialog'
import { CodeSnippet } from '../common/CodeSnippet'
import {
  CenteredSpinner,
  DialogActivator,
  InternalLink,
  ShortDate,
  ShowIntercomLink,
  useDialogActivator,
  useShopFlag,
  useShopPermission,
} from '../common/helpers'
import { FeatureAccessFlag } from '../common/SystemData'
import { UpgradeBanner } from '../common/UpgradeBanner'
import * as urls from '../common/urls'
import { MerchantApiIpAccessSection } from './MerchantApiIpAccessSection'
import styles from './MerchantApiTokensCard.module.css'

interface MerchantApiToken {
  id: number
  title: string
  hint: string
  value: string | null
  createdAt: string
}

const useTokens = () =>
  useSWR<MerchantApiToken[]>(['merchant-api-tokens'], () =>
    backendFetchResultData('GET', '/merchant-api-tokens')
  )

const mutateTokens = (res: Result<MerchantApiToken[]>) =>
  res.data && mutate(['merchant-api-tokens'], res.data, { revalidate: false })

const TokenCreatedModal = ({ token, onClose }: { token: MerchantApiToken; onClose: () => any }) => (
  <AdaptiveModal
    title={`API token: ${token.title}`}
    open={true}
    onClose={onClose}
    sectioned
    primaryAction={{ content: 'Done', onAction: onClose }}
  >
    <LegacyStack vertical>
      <Banner tone="warning">
        Copy this token now and store it somewhere safe. For security reasons it won't be shown
        again.
      </Banner>
      <CodeSnippet code={token.value ?? ''} />
    </LegacyStack>
  </AdaptiveModal>
)

const TokenCreateModal = ({ activator }: { activator: DialogActivator }) => {
  const [title, setTitle] = useState('')
  const [createdToken, setCreatedToken] = useState<MerchantApiToken | null>(null)

  const actionState = useApiActionState({
    method: 'POST',
    url: '/merchant-api-tokens',
    opts: { body: JSON.stringify({ title }) },
    onSuccess: (res: Result<MerchantApiToken>) => {
      mutate(['merchant-api-tokens'])
      activator.close()
      setTitle('')
      setCreatedToken(res.data ?? null)
    },
    bannerPosition: 'external',
  })

  return (
    <>
      {createdToken && (
        <TokenCreatedModal token={createdToken} onClose={() => setCreatedToken(null)} />
      )}
      <AdaptiveModal
        title="Create API token"
        open={activator.open}
        onClose={activator.close}
        sectioned
        primaryAction={{
          content: 'Create',
          disabled: !title,
          onAction: actionState.run,
          loading: actionState.loading,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: activator.close }]}
      >
        <ApiActionBanner
          state={actionState}
          intercomMessage="I need help setting up my API token."
        />
        <TextField
          id="title"
          name="title"
          label="Title"
          value={title}
          onChange={setTitle}
          autoComplete="off"
          maxLength={12}
          requiredIndicator={true}
        />
      </AdaptiveModal>
      <ApiAction state={actionState} />
    </>
  )
}

const TokenList = () => {
  const { data: tokens } = useTokens()
  const [tokenToRemove, setTokenToRemove] = useState<MerchantApiToken | null>()
  const tokenRemoveActivator = useDialogActivator()

  if (tokens === undefined) {
    return <CenteredSpinner />
  }

  return (
    <>
      {tokens.length > 0 && (
        <DataTable
          headings={['Created at', 'Title', 'Token', 'Actions']}
          columnContentTypes={['text', 'text', 'text', 'numeric']}
          verticalAlign="middle"
          rows={tokens.map((token) => [
            <ShortDate date={token.createdAt} />,
            <LegacyStack.Item fill>{token.title}</LegacyStack.Item>,
            <Text as="span">
              <code>{`••••${token.hint}`}</code>
            </Text>,
            <ButtonGroup variant="segmented">
              <Button
                accessibilityLabel={`Remove ${token.title}`}
                icon={DeleteIcon}
                onClick={() => {
                  setTokenToRemove(token)
                  tokenRemoveActivator.show()
                }}
                variant="primary"
                tone="critical"
              />
            </ButtonGroup>,
          ])}
        ></DataTable>
      )}
      {tokenToRemove && (
        <ApiActionDialog
          activator={tokenRemoveActivator}
          message={`Do you want to permanently remove "${tokenToRemove.title}" API token?`}
          primaryButtonTitle="Remove"
          destructive={true}
          url={`/merchant-api-tokens/${tokenToRemove.id}`}
          method="DELETE"
          onClose={() => setTokenToRemove(null)}
          onSuccess={mutateTokens}
        />
      )}
    </>
  )
}

const UpgradeToAccess = () => (
  <>
    Please{' '}
    <InternalLink url={urls.pricingPlansUrl()} target={'_blank'}>
      upgrade
    </InternalLink>{' '}
    to experience the advanced capabilities of the PayWhirl for Shopify API, available for merchants
    on our <Text as="strong">Traction Plan</Text> or higher.{' '}
  </>
)

const RequestAccessCard = ({ apiAllowed }: { apiAllowed: boolean }) => (
  <LegacyCard sectioned>
    <UpgradeBanner>
      {!apiAllowed && <UpgradeToAccess />}
      To activate your API access, kindly <ShowIntercomLink>get in touch</ShowIntercomLink> with the
      PayWhirl support team. Share your use case with us, and we'll set up your API access, opening
      up new possibilities for your e-commerce strategy.
    </UpgradeBanner>
  </LegacyCard>
)

export const MerchantApiTokensCard = () => {
  const createActivator = useDialogActivator()
  const merchantApiAccess = useShopFlag('merchant_api_access') as FeatureAccessFlag
  const apiAllowed = useShopPermission('apiAllowed')

  if (merchantApiAccess === 'disabled') {
    return <RequestAccessCard apiAllowed={apiAllowed} />
  }

  if (merchantApiAccess === 'by-plan' && !apiAllowed) {
    return (
      <LegacyCard sectioned>
        <UpgradeBanner>
          <UpgradeToAccess />
        </UpgradeBanner>
      </LegacyCard>
    )
  }

  return (
    <div className={styles.wrapper}>
      <LegacyCard>
        <TokenList />
        <LegacyCard.Section>
          <Button onClick={createActivator.show}>Create API token</Button>
        </LegacyCard.Section>
        <Divider />
        <MerchantApiIpAccessSection />
        <TokenCreateModal activator={createActivator} />
      </LegacyCard>
    </div>
  )
}
